/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import {
	DeviceLabels,
	MeetingProvider,
	useMeetingManager,
	useLocalVideo,
	useContentShareState,
	useLogger,
} from "amazon-chime-sdk-component-library-react";
import MeetingComponent from "./Meeting";
import React, { useEffect, useMemo } from "react";
import { createMeetingAndAddCustomerAndAgentAttendee } from "../../services/VideoMeetingService";
import { MeetingSessionConfiguration, LogLevel } from "amazon-chime-sdk-js";
import { MEETING_ACTIONS } from "../../../../utility/constants";
import { MeetingButton } from "./MeetingButton";
import { withStyles } from "@material-ui/core";
import styles from "./styles";
import { VideoMeetingEventProvider, useVideoMeetingEvents } from "./VideoMeetingEventProvider";
import { valToBool } from "../../utility";
import useRemoteSession from "./EndMeetingControl/useRemoteSession";

const MeetingTabContent = ({ classes, attributes }) => {
	const meetingManager = useMeetingManager();
	const { isVideoEnabled, toggleVideo } = useLocalVideo();
	const { isLocalUserSharing } = useContentShareState();
	useRemoteSession();
	const logger = useLogger();
	const {
		removeMeetingRequest,
		activeContactId,
		inMeetingContactId,
		isMeetingRequested,
		setInMeetingContactId,
		sendMessage,
	} = useVideoMeetingEvents();

	useEffect(() => {
		if (isLocalUserSharing) {
			if (isVideoEnabled) {
				toggleVideo();
			}
		}
	}, [isLocalUserSharing, isVideoEnabled, toggleVideo]);

	const joinMeeting = async () => {
		logger.setLogLevel(LogLevel.OFF);
		const joinInfo = await createMeetingAndAddCustomerAndAgentAttendee({ contactId: activeContactId });
		const { Meeting, Attendees } = joinInfo;
		const meetingConfiguration = new MeetingSessionConfiguration(Meeting, Attendees[0]);

		await meetingManager.join(meetingConfiguration, { enableWebAudio: true, deviceLabels: DeviceLabels.AudioAndVideo });
		meetingManager.audioVideo?.realtimeMuteLocalAudio?.();
		const meetingInfo = isMeetingRequested ? MEETING_ACTIONS.AGENT_ACCEPTED : MEETING_ACTIONS.AGENT_INIT;
		removeMeetingRequest(activeContactId);
		await sendMessage(meetingInfo);
	};

	const handleInitMeeting = async () => {
		if (activeContactId && activeContactId === inMeetingContactId) {
			throw new Error("Call already in progress");
		}
		setInMeetingContactId(activeContactId);
		await joinMeeting(activeContactId);
		await meetingManager.start();
	};

	const isCustomerMediaSupported = useMemo(() => {
		if (!attributes) {
			return false;
		}

		const matchValue = attributes.find((x) => x.name === "isSupportedMediaPlatform")?.value;
		return valToBool(matchValue);
	}, [attributes]);

	return (
		<>
			{!inMeetingContactId || !activeContactId || inMeetingContactId !== activeContactId ? (
				<MeetingButton
					classes={classes}
					handleInitMeeting={handleInitMeeting}
					isCustomerMediaSupported={isCustomerMediaSupported}
				/>
			) : (
				<MeetingComponent classes={classes} />
			)}
		</>
	);
};

const MeetingPage = withStyles(styles)(MeetingTabContent);

export default (props) => (
	<MeetingProvider>
		<VideoMeetingEventProvider>
			<MeetingPage {...props} />
		</VideoMeetingEventProvider>
	</MeetingProvider>
);
